import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { ChangeVehicleStatusInput, InventoryStatus, VehicleDetail } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { formatMoney } from '../../../lib/format';
import { refusalReasons, serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

export interface StatusDialogProps {
  vehicle: VehicleDetail;
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

interface StatusForm {
  to: InventoryStatus | '';
  note: string;
  salePrice: string;
  saleDate: string;
  buyerName: string;
  buyerPhone: string;
  buyerContactId: string;
  saleNote: string;
}

const initialForm = (vehicle: VehicleDetail): StatusForm => ({
  to: (vehicle.availableTransitions.find((transition) => transition.allowed)?.to ?? '') as InventoryStatus | '',
  note: '',
  // The asking price is what CTA meant to get for the car, so it is the figure a salesperson starts from
  // and negotiates down — not an empty field they have to go and look up.
  salePrice: vehicle.askingPrice === null ? '' : String(vehicle.askingPrice),
  // The tenant's business day, as the server computed it for this dossier — never the browser's clock.
  saleDate: vehicle.valuation.valuationDate,
  buyerName: '',
  buyerPhone: '',
  buyerContactId: '',
  saleNote: '',
});

const buyerGiven = (form: StatusForm): boolean => form.buyerContactId !== '' || (form.buyerName.trim() !== '' && form.buyerPhone.trim().length >= 5);

/**
 * How long the typed price and date settle before the preview is asked for. The same 400 ms the
 * estimator's autosave waits: long enough that a burst of typing is one question.
 */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * "Update inventory status" (plan §5.6, §7; prototype `inventoryStatusModal` / `inventorySaleFields`).
 *
 * One dialog, two shapes, decided by the target: a **note** for a move between the warehouse and the
 * workshop, and the **sale** — price, date, buyer — for `SOLD`.
 *
 * The branch worth reading is the below-retail one, and **the page does not decide it**. As the price
 * and the date settle, `vehicles.salePreview` answers with the required retail of the vehicle priced
 * *as sold* — the figure `changeStatus` will hold the sale to. From that answer: an administrator sees
 * an amber alert and a reason field, a salesperson is told in one line that this is not theirs to do,
 * and the button is disabled rather than left to fail in front of a customer. While no answer describes
 * what is on screen the button stays live, because the service runs the same rule inside the
 * transaction and refusing here on a guess is the worse mistake.
 */
export const StatusDialog = ({ vehicle, isOpen, onClose, onChanged }: StatusDialogProps) => {
  const { t } = useI18nTranslation();
  const [form, setForm] = useState<StatusForm>(() => initialForm(vehicle));
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);

  const changeStatus = useMutation(orpc.vehicles.changeStatus.mutationOptions());

  useEffect(() => {
    if (isOpen) {
      setForm(initialForm(vehicle));
      setError(null);
      setReasons([]);
    }
  }, [isOpen, vehicle]);

  const isSale = form.to === 'SOLD';
  const salePrice = form.salePrice.trim() === '' ? null : Number(form.salePrice);
  const priceGiven = salePrice !== null && Number.isFinite(salePrice) && salePrice > 0;

  /**
   * The figures the preview was last asked about. The server answers "below the required retail?" from
   * the vehicle priced **as sold**, which this page deliberately cannot work out for itself — holding
   * stops on the sale date, and a second copy of that rule in the browser is exactly how the dialog came
   * to block sales the service would have accepted (plan §5.6).
   */
  const [asked, setAsked] = useState<{ salePrice: number; saleDate: string } | null>(null);

  useEffect(() => {
    if (!isSale || !priceGiven || form.saleDate === '') {
      setAsked(null);

      return;
    }

    const timer = setTimeout(() => setAsked({ salePrice: salePrice as number, saleDate: form.saleDate }), PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [isSale, priceGiven, salePrice, form.saleDate]);

  const previewQuery = useQuery(
    orpc.vehicles.salePreview.queryOptions({
      input: { vehicleId: vehicle.id, salePrice: asked?.salePrice ?? 0, saleDate: asked?.saleDate ?? '' },
      enabled: asked !== null,
    }),
  );

  // An answer about the previous keystroke would flag the wrong number, so a preview that no longer
  // describes what is on screen counts as no answer at all: the button stays live and the service, which
  // runs the same rule inside the transaction, has the last word.
  const preview =
    asked !== null && previewQuery.data !== undefined && asked.salePrice === salePrice && asked.saleDate === form.saleDate ? previewQuery.data : null;

  /** The preview was asked and answered with an error: said out loud, because a silent gap reads as "fine". */
  const previewUnavailable = asked !== null && asked.salePrice === salePrice && asked.saleDate === form.saleDate && previewQuery.isError;

  const requiredRetail = preview?.requiredRetail ?? null;
  const belowRetail = preview?.belowRetail ?? false;
  /** Below the required retail **and** the caller is not an administrator as the database has them. */
  const adminOnly = preview?.adminOnly ?? false;
  const noteRequired = preview?.noteRequired ?? false;

  // A search only starts once somebody has typed enough to mean it; a blank box asks for nothing.
  const search = `${form.buyerName} ${form.buyerPhone}`.trim();
  const searching = isSale && form.buyerContactId === '' && search.length >= 3;

  const contactsQuery = useQuery(orpc.contacts.search.queryOptions({ input: { page: 1, pageSize: 5, q: search }, enabled: searching }));

  const matches = searching ? (contactsQuery.data?.items ?? []) : [];

  const transition = vehicle.availableTransitions.find((candidate) => candidate.to === form.to) ?? null;
  const blockers = useMemo(
    () => [...new Set([...(transition?.blockers ?? []), ...(preview?.blockers ?? []), ...reasons])],
    [transition, preview, reasons],
  );

  const canSubmit =
    form.to !== '' &&
    (transition?.allowed ?? false) &&
    (isSale
      ? priceGiven && form.saleDate !== '' && buyerGiven(form) && !adminOnly && (!noteRequired || form.saleNote.trim() !== '')
      : form.note.trim() !== '');

  const submit = async () => {
    setError(null);
    setReasons([]);

    const input: ChangeVehicleStatusInput = isSale
      ? {
          vehicleId: vehicle.id,
          expectedRevision: vehicle.revision,
          to: 'SOLD',
          sale: {
            salePrice: Number(form.salePrice),
            saleDate: form.saleDate,
            ...(form.buyerContactId === ''
              ? { buyer: { name: form.buyerName.trim(), phone: form.buyerPhone.trim() } }
              : { buyerContactId: form.buyerContactId }),
            ...(form.saleNote.trim() === '' ? {} : { note: form.saleNote.trim() }),
          },
        }
      : { vehicleId: vehicle.id, expectedRevision: vehicle.revision, to: form.to as InventoryStatus, note: form.note.trim() };

    try {
      await changeStatus.mutateAsync(input);
      await onChanged();
      onClose();
    } catch (caught: unknown) {
      setReasons(refusalReasons(caught));
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('vehicles.statusDialogTitle', { stockNo: vehicle.stockNo })}
      description={t('vehicles.statusDialogBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={changeStatus.isPending}
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {isSale ? t('vehicles.recordSale') : t('vehicles.saveStatus')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <FormField
          id="status-target"
          label={t('vehicles.statusTarget')}
        >
          <Select
            id="status-target"
            value={form.to}
            onChange={(event) => setForm((current) => ({ ...current, to: event.target.value as InventoryStatus }))}
            options={vehicle.availableTransitions.map((candidate) => ({
              value: candidate.to,
              label: t(`enums.inventoryStatus.${candidate.to}`),
              disabled: !candidate.allowed,
            }))}
          />
        </FormField>

        {isSale ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                id="status-sale-price"
                label={t('vehicles.salePrice')}
                hint={requiredRetail === null ? undefined : t('vehicles.requiredRetailIs', { amount: formatMoney(requiredRetail) })}
              >
                <Input
                  id="status-sale-price"
                  type="number"
                  min={0.001}
                  step={0.001}
                  dir="ltr"
                  value={form.salePrice}
                  onChange={(event) => setForm((current) => ({ ...current, salePrice: event.target.value }))}
                />
              </FormField>
              <FormField
                id="status-sale-date"
                label={t('vehicles.saleDate')}
              >
                <Input
                  id="status-sale-date"
                  type="date"
                  dir="ltr"
                  max={vehicle.valuation.valuationDate}
                  {...(vehicle.purchaseDate === null ? {} : { min: vehicle.purchaseDate })}
                  value={form.saleDate}
                  onChange={(event) => setForm((current) => ({ ...current, saleDate: event.target.value }))}
                />
              </FormField>
            </div>

            <div className="space-y-2">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  id="status-buyer-name"
                  label={t('vehicles.buyerName')}
                >
                  <Input
                    id="status-buyer-name"
                    value={form.buyerName}
                    onChange={(event) => setForm((current) => ({ ...current, buyerName: event.target.value, buyerContactId: '' }))}
                  />
                </FormField>
                <FormField
                  id="status-buyer-phone"
                  label={t('vehicles.buyerPhone')}
                  hint={t('estimates.customerPhoneHint')}
                >
                  <Input
                    id="status-buyer-phone"
                    dir="ltr"
                    inputMode="tel"
                    value={form.buyerPhone}
                    onChange={(event) => setForm((current) => ({ ...current, buyerPhone: event.target.value, buyerContactId: '' }))}
                  />
                </FormField>
              </div>

              {/* Picking a match links **that** contact: the service never renames somebody who has
                  bought before, so their spelling survives this sale (plan §11 item 11). */}
              {matches.length === 0 ? null : (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{t('vehicles.buyerMatches')}</p>
                  <ul className="space-y-1">
                    {matches.map((contact) => (
                      <li key={contact.id}>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((current) => ({ ...current, buyerContactId: contact.id, buyerName: contact.name, buyerPhone: contact.phone }))
                          }
                          className="w-full rounded-md border border-border px-3 py-1.5 text-start text-sm transition-colors hover:bg-muted"
                        >
                          {contact.name} · <span dir="ltr">{contact.phone}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {form.buyerContactId === '' ? null : (
                <p className="text-xs text-muted-foreground">{t('vehicles.buyerLinked', { name: form.buyerName })}</p>
              )}
            </div>

            {previewUnavailable ? (
              <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{t('vehicles.previewUnavailable')}</p>
            ) : null}

            {belowRetail ? (
              !adminOnly ? (
                <>
                  <p
                    role="alert"
                    className="rounded-md border border-[#a16207]/30 bg-[#a16207]/5 px-3 py-2 text-sm text-[#a16207]"
                  >
                    {t('vehicles.belowRetailAdmin', { amount: formatMoney(requiredRetail) })}
                  </p>
                  <FormField
                    id="status-sale-note"
                    label={t('vehicles.belowRetailReason')}
                  >
                    <Textarea
                      id="status-sale-note"
                      rows={3}
                      value={form.saleNote}
                      onChange={(event) => setForm((current) => ({ ...current, saleNote: event.target.value }))}
                    />
                  </FormField>
                </>
              ) : (
                <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">{t('vehicles.belowRetailSales')}</p>
              )
            ) : null}
          </>
        ) : (
          <FormField
            id="status-note"
            label={t('vehicles.statusNote')}
            hint={t('vehicles.statusNoteHint')}
          >
            <Textarea
              id="status-note"
              rows={3}
              value={form.note}
              onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
            />
          </FormField>
        )}

        {blockers.length === 0 ? null : (
          <ul className="list-inside list-disc space-y-1 text-xs text-muted-foreground">
            {blockers.map((blocker) => (
              <li key={blocker}>{t(`vehicles.statusBlockers.${blocker}`, { defaultValue: blocker })}</li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
};
